
import React, { useEffect, useState, ChangeEvent, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { usePartsStore } from "../../store/partsStore";
import type { PartsTable } from "../../models/Partslist";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

ModuleRegistry.registerModules([AllCommunityModule]);

const PartPricing: React.FC = () => {
  const { parts, fetchParts, updateParts } = usePartsStore();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [filterText, setFilterText] = useState("");

  const [editRowId, setEditRowId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<PartsTable>>({});
  const [loadingParts, setLoadingParts] = useState(true);

  useEffect(() => {
    let mounted = true;
    const loadParts = async () => {
      setLoadingParts(true);
      try {
        await fetchParts();
      } finally {
        if (mounted) setLoadingParts(false);
      }
    };
    void loadParts();
    return () => {
      mounted = false;
    };
  }, [fetchParts]);

  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };

  const filteredParts = parts.filter((row) =>
    Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(filterText.toLowerCase())
  );

  const totalItems = filteredParts.length;
  const totalPages = Math.max(1, Math.ceil(Math.max(1, totalItems) / rowsPerPage));
  const startItem = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = totalItems === 0 ? 0 : Math.min((page + 1) * rowsPerPage, totalItems);
  const pageWindowSize = 5;
  const pageWindowStart = Math.floor(page / pageWindowSize) * pageWindowSize;
  const pageWindowEnd = Math.min(pageWindowStart + pageWindowSize, totalPages);
  const visiblePageIndexes = Array.from(
    { length: Math.max(0, pageWindowEnd - pageWindowStart) },
    (_, idx) => pageWindowStart + idx
  );

  const paginatedParts = filteredParts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // start edit for a row
  const startRowEdit = (row: PartsTable) => {
    setEditRowId(row.partId);
    setEditData({ ...row });
  };

  const cancelEdit = () => {
    setEditRowId(null);
    setEditData({});
  };

  const handleSave = async () => {
    if (editRowId === null) return;
    const uomCandidate = String((editData as any)?.uom ?? "").trim();
    if (uomCandidate.length < 2) {
      setSnackbar({
        open: true,
        message: "UOM must be at least 2 characters.",
        severity: "error",
      });
      return;
    }
    try {
      const payload = { ...(editData as PartsTable), partId: editRowId } as PartsTable;
      const response = await updateParts([payload]);
      await fetchParts();
      setSnackbar({
        open: true,
        message: (response && (response as any).message) || "Update successful",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to update part",
        severity: "error",
      });
    } finally {
      setEditRowId(null);
      setEditData({});
    }
  };

  // commit inline edits when a field loses focus
  const onInlineCommit = (field: keyof PartsTable, value: any) => {
    setEditData((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isCurrentEditUomValid = (() => {
    if (editRowId === null) return true;
    const uomCandidate = String((editData as any)?.uom ?? "").trim();
    return uomCandidate.length >= 2;
  })();

  // cell renderer that shows TextField when row is in edit mode; auto-focus via inputRefs
  const editableCellRenderer =
    (field: keyof PartsTable) =>
    (params: any) => {
      const row = params.data as PartsTable;
      const isEditing = editRowId === row.partId;

      if (isEditing) {
        const value = (editData && (editData as any)[field]) ?? (row as any)[field] ?? "";
        return (
          <TextField
            size="small"
            defaultValue={value}
            onBlur={(e) => onInlineCommit(field, e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            inputProps={{ style: { fontSize: 13, padding: "6px 8px" } }}
            variant="outlined"
          />
        );
      } else {
        const display = (row as any)[field] ?? "";
        return <span style={{ padding: "4px 8px", display: "inline-block" }}>{display}</span>;
      }
    };

  const columnDefs: ColDef<PartsTable>[] = useMemo(
    () => [
      { headerName: "Part Number", field: "partNumber", flex: 1 },
      { headerName: "Description", field: "description", flex: 1 },
      {
        headerName: "UOM",
        field: "uom",
        flex: 0.8,
        cellRenderer: editableCellRenderer("uom"),
      },
      {
        headerName: "Price",
        field: "price",
        flex: 0.8,
        cellRenderer: editableCellRenderer("price"),
      },
      {
        headerName: "Action",
        field: "partId",
        flex: 0.8,
        cellRenderer: (params: any) => {
          const row = params.data as PartsTable;
          const isEditing = editRowId === row.partId;

          return (
            <Box display="flex" gap={1} alignItems="center" justifyContent="flex-end" pr={1}>
              {isEditing ? (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSave()}
                    disabled={!isCurrentEditUomValid}
                    sx={{ textTransform: "none", fontSize: 13 }}
                  >
                    Save
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => cancelEdit()}
                    title="Cancel"
                    sx={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      bgcolor: "#fff",
                      ml: 0.5,
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => startRowEdit(row)}
                  sx={{ textTransform: "none", fontSize: 13 }}
                >
                  Edit
                </Button>
              )}
            </Box>
          );
        },
      },
    ],
    [editRowId, editData]
  );

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h4" gutterBottom>
        Parts Pricing
      </Typography>

      <TextField
        label="Search Parts"
        variant="outlined"
        size="small"
        value={filterText}
        onChange={handleFilterChange}
        style={{ width: "300px", float: "right", marginBottom: "10px" }}
      />

      {loadingParts ? (
        <Box
          sx={{
            clear: "both",
            minHeight: 320,
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            bgcolor: "#fff",
          }}
        >
          <CircularProgress size={24} />
          <Typography sx={{ fontSize: 14, color: "#4D4D4D" }}>Loading parts pricing...</Typography>
        </Box>
      ) : (
        <>
          <div
            className="ag-theme-alpine"
            style={{
              height: "auto",
              width: "100%",
              clear: "both",
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              color: "#333",
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            }}
          >
            <AgGridReact
              rowData={paginatedParts}
              columnDefs={columnDefs}
              domLayout="autoHeight"
              headerHeight={40}
              rowHeight={48}
              defaultColDef={{
                flex: 1,
                editable: false,
                resizable: true,
                cellStyle: { display: "flex", alignItems: "center", padding: "4px 8px" },
                headerClass: "ag-header-cell",
              }}
              getRowStyle={(params) => ({
                backgroundColor:
                  params.node.rowIndex != null && params.node.rowIndex % 2 === 0 ? "#fafafa" : "#fff",
                transition: "background-color 0.2s",
              })}
              getRowId={(params) => String((params.data as PartsTable).partId)}
              suppressClickEdit
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 14,
              alignItems: "center",
              borderTop: "1px solid #E0E0E0",
              background: "#FFF",
              borderRadius: 8,
              padding: "16px 20px",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#4D4D4D" }}>
                Showing {startItem}-{endItem} of {totalItems} parts
              </Typography>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#4D4D4D" }}>
                  Page size
                </Typography>
                <div style={{ position: "relative" }}>
                  <select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    style={{
                      minWidth: 92,
                      height: 36,
                      border: "1px solid #E0E0E0",
                      borderRadius: 6,
                      padding: "0 30px 0 12px",
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#1A1A1A",
                      appearance: "none",
                      background: "#FFF",
                      cursor: "pointer",
                    }}
                  >
                    <option value={5}>5 rows</option>
                    <option value={10}>10 rows</option>
                    <option value={25}>25 rows</option>
                  </select>
                  <KeyboardArrowDownIcon
                    fontSize="small"
                    sx={{
                      position: "absolute",
                      right: 6,
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      color: "#1A1A1A",
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1A1A1A" }}>
                Page {page + 1} of {totalPages}
              </Typography>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <IconButton
                  size="small"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 0}
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid transparent",
                    opacity: page === 0 ? 0.45 : 1,
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>

                {visiblePageIndexes.map((i) => {
                  const active = i === page;
                  return (
                    <Button
                      key={i}
                      size="small"
                      onClick={() => handlePageChange(i)}
                      sx={{
                        minWidth: 36,
                        height: 36,
                        borderRadius: "8px",
                        border: active ? "1px solid #003366" : "1px solid transparent",
                        bgcolor: active ? "#003366" : "transparent",
                        color: active ? "#FFF" : "#4D4D4D",
                        fontSize: 14,
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: active ? "#003366" : "rgba(0,0,0,0.04)",
                          borderColor: active ? "#003366" : "transparent",
                        },
                      }}
                    >
                      {i + 1}
                    </Button>
                  );
                })}

                <IconButton
                  size="small"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages - 1}
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid transparent",
                    opacity: page >= totalPages - 1 ? 0.45 : 1,
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </div>
            </div>
          </div>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PartPricing;
